'use client';

import { useEffect, useState } from 'react';
import { motion } from 'framer-motion';

/**
 * Fixed vertical chapter rail (right side, desktop only).
 * Each item links to an element id; the rail highlights the section
 * currently most centered in the viewport using IntersectionObserver.
 */
export default function ChapterRail({
  items,
}: {
  items: { id: string; label: string }[];
}) {
  const [active, setActive] = useState(items[0]?.id ?? '');
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    if (typeof window === 'undefined') return;
    const onScroll = () => setVisible(window.scrollY > window.innerHeight * 0.5);
    onScroll();
    window.addEventListener('scroll', onScroll, { passive: true });

    const obs = new IntersectionObserver(
      (entries) => {
        const vis = entries
          .filter((e) => e.isIntersecting)
          .sort((a, b) => b.intersectionRatio - a.intersectionRatio);
        if (vis[0]) setActive(vis[0].target.id);
      },
      { rootMargin: '-40% 0px -40% 0px', threshold: [0, 0.25, 0.5, 0.75, 1] }
    );
    items.forEach((it) => {
      const el = document.getElementById(it.id);
      if (el) obs.observe(el);
    });

    return () => {
      window.removeEventListener('scroll', onScroll);
      obs.disconnect();
    };
  }, [items]);

  return (
    <motion.aside
      aria-hidden="true"
      initial={false}
      animate={{ opacity: visible ? 1 : 0, x: visible ? 0 : 12 }}
      transition={{ duration: 0.4, ease: [0.16, 1, 0.3, 1] }}
      className="hidden lg:flex fixed right-6 top-1/2 -translate-y-1/2 z-40 pointer-events-none"
    >
      <ul className="flex flex-col gap-3 pointer-events-auto">
        {items.map((it) => {
          const isActive = it.id === active;
          return (
            <li key={it.id}>
              <a
                href={`#${it.id}`}
                className="group flex items-center gap-3 justify-end"
              >
                <span
                  className={`text-[10px] tracking-[0.28em] uppercase font-medium transition-all duration-300 ${
                    isActive ? 'text-purple-700 opacity-100 translate-x-0' : 'text-gray-400 opacity-0 group-hover:opacity-100 translate-x-2 group-hover:translate-x-0'
                  }`}
                >
                  {it.label}
                </span>
                <span
                  className={`block rounded-full transition-all duration-300 ${
                    isActive ? 'h-2.5 w-2.5 bg-purple-600 shadow-[0_0_0_4px_rgba(124,58,237,0.18)]' : 'h-1.5 w-1.5 bg-gray-300 group-hover:bg-purple-500'
                  }`}
                />
              </a>
            </li>
          );
        })}
      </ul>
    </motion.aside>
  );
}
